import { PencomBanner } from "@/components/banners/PencomBanner";
import { PencomCard } from "@/components/cards/PencomCard";
import { EmployeePensionRecord } from "@/components/dashboard/EmployeePensionRecord";
import { RemoveEmployeeDialog } from "@/components/dashboard/RemoveEmployeeDialog";
import {
  useGetEmployees,
  useCreateChangeRequest,
} from "@/hooks/employees/employees.hook";
import { useState, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { PageSkeleton } from "@/components/common/SkeletonLoader";
import { ChangeRequestType } from "@/constants/enums/change-request.enum";
import type { Employee } from "@/models/responses/employees/employees.response";

export const EmployeeListPage = () => {
  const [limit] = useState(10);
  const [page, setPage] = useState(1);
  const [banner, setBanner] = useState<{
    type: "success" | "warning" | "error";
    message: string;
  } | null>(null);
  const [employeeToRemove, setEmployeeToRemove] = useState<Employee | null>(
    null
  );

  const { data, isLoading } = useGetEmployees(page, limit);
  const queryClient = useQueryClient();
  const { mutate: createChangeRequest, isPending } = useCreateChangeRequest();

  const handleRemove = (employee: Employee) => {
    setEmployeeToRemove(employee);
  };

  const handleConfirmRemoval = (reason: string, documentUrls: string[], comment: string) => {
    if (!employeeToRemove) return;
    createChangeRequest(
      {
        type: ChangeRequestType.REMOVAL,
        employeeId: employeeToRemove.id,
        reason,
        ...(documentUrls.length > 0 && { supportingDocuments: documentUrls }),
                ...(comment && { comment }),
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ["employees"] });
          setBanner({
            type: "warning",
            message: `Your request to remove employee ${employeeToRemove.fullName} is under review.`,
          });
          setEmployeeToRemove(null);
        },
        onError: () => {
          setBanner({
            type: "error",
            message: `Unable to remove employee ${employeeToRemove.fullName}. Please try again.`,
          });
          setEmployeeToRemove(null);
        },
      }
    );
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const currentPageData = useMemo(() => {
    const pageData = data?.data || [];
    return [...pageData].sort((a, b) => {
      const dateA = new Date(a.dateOfAppointment).getTime();
      const dateB = new Date(b.dateOfAppointment).getTime();
      return dateA - dateB;
    });
  }, [data]);

  if (isLoading) {
    return <PageSkeleton tableColumns={5} tableRows={8} />;
  }

  return (
    <div className="flex flex-col gap-y-6 max-w-[1240px] mx-auto mt-6">
      {banner && (
        <PencomBanner
          type={banner.type}
          title={banner.message}
          onClose={() => setBanner(null)}
        />
      )}
      <div className="flex flex-row justify-between items-center">
        <div className="flex flex-col gap-y-2">
          <h2 className="font-clash-display text-black-dark-grey text-2xl font-semibold">
            Employees
          </h2>
        </div>
        {/* <Dropdown
                    trigger={
                        <div className="flex flex-row items-center gap-x-2 bg-primary-button text-white font-clash-display text-sm font-medium px-4 py-3 rounded-lg cursor-pointer hover:scale-105 transition-transform">
                            <span>Add New Employee</span>
                            <img src="/assets/icons/dropdown-arrow-white.svg" alt="arrow" />
                        </div>
                    }
                    items={[
                        {
                            id: 'manual',
                            label: 'Manually Add Employee',
                            icon: '/assets/icons/write.svg',
                            onClick: () => navigate(PencomRoutes.ADD_EMPLOYEE),
                        },
                        // {
                        //     id: 'bulk',
                        //     label: 'Bulk Upload',
                        //     icon: '/assets/icons/upload.svg',
                        //     onClick: () => navigate(PencomRoutes.BULK_UPLOAD_EMPLOYEE),
                        // },
                    ]}
                />*/}
      </div>
      <PencomCard extraClassName="justify-between">
        <EmployeePensionRecord
          data={currentPageData}
          currentPage={page}
          totalPages={data?.pagination?.totalPages || 1}
          onPageChange={handlePageChange}
          showPagination={true}
          visibleColumns={[
            "fullName",
            "rsaPin",
            "pfa",
            "designation",
            "totalSalary",
            "status",
            "remove",
          ]}
          onRemove={handleRemove}
        />
      </PencomCard>

      <RemoveEmployeeDialog
        open={employeeToRemove !== null}
        onOpenChange={(open) => {
          if (!open) setEmployeeToRemove(null);
        }}
        employeeName={employeeToRemove?.fullName ?? ""}
        isLoading={isPending}
        onConfirm={handleConfirmRemoval}
      />
    </div>
  );
};
